import { Component, Inject, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatCalendar, MatCalendarCellClassFunction } from '@angular/material/datepicker'
import { Store } from '@ngrx/store'
import { ActionFailed, Toaster } from '@tokeet-frontend/tv3-platform'
import {
  AirbnbBlackoutDateRange,
  AirbnbChannelService,
  AirbnbListingPromotionProgramStatus,
  AirbnbTopRatedGuestDiscountPromotionType,
  getAirbnbBlackoutDatesYearQuotas,
  toAirbnbBlackoutDateRanges,
} from '@tokeet-frontend/channels'
import { Connection } from '@tv3/store/connection/connection.model'
import * as lodash from 'lodash'
import * as moment from 'moment'
import { finalize } from 'rxjs/operators'

export interface AirbnbBlackoutDatesDialogData {
  connection: Connection
  discount: AirbnbListingPromotionProgramStatus
}

@Component({
  selector: 'app-airbnb-blackout-dates-dialog',
  templateUrl: './airbnb-blackout-dates-dialog.component.html',
  styleUrls: ['./airbnb-blackout-dates-dialog.component.scss'],
  host: { class: 'modal-content' },
})
export class AirbnbBlackoutDatesDialogComponent {
  @ViewChild(MatCalendar) calendar: MatCalendar<moment.Moment>

  discountName =
    this.data.discount.type === AirbnbTopRatedGuestDiscountPromotionType
      ? $localize`Top-Rated Guests Discount`
      : $localize`Mobile-Only Discount`

  // Airbnb rejects past dates and accepts dates up to 2 years ahead.
  minDate = moment().startOf('day')
  maxDate = moment().add(2, 'years')
  today = this.minDate.format('YYYY-MM-DD')

  savedDates = this.data.discount.blackout_dates || []
  selected = new Set(this.savedDates)
  ranges = toAirbnbBlackoutDateRanges(this.savedDates)
  quotas = getAirbnbBlackoutDatesYearQuotas(this.data.discount)
  datesToAdd: string[] = []
  datesToRemove: string[] = []

  // mat-calendar does not pass the click event, so the wrapper records the Shift key.
  shiftKey = false
  lastClickedDate: string
  isLoading = false

  dateClass: MatCalendarCellClassFunction<moment.Moment> = (date, view) =>
    view === 'month' && this.selected.has(date.format('YYYY-MM-DD')) ? 'blackout-date' : ''

  constructor(
    public dialogRef: MatDialogRef<AirbnbBlackoutDatesDialogComponent>,
    private toaster: Toaster,
    private store: Store<any>,
    private airbnbService: AirbnbChannelService,
    @Inject(MAT_DIALOG_DATA) public data: AirbnbBlackoutDatesDialogData
  ) {}

  // A click toggles one date. A Shift+click gives the same state to all dates from the last clicked date.
  onDateClick(date: moment.Moment) {
    const clicked = date.format('YYYY-MM-DD')
    const blackout = !this.selected.has(clicked)
    const from = this.shiftKey && this.lastClickedDate ? lodash.min([this.lastClickedDate, clicked]) : clicked
    const to = this.shiftKey && this.lastClickedDate ? lodash.max([this.lastClickedDate, clicked]) : clicked
    this.lastClickedDate = clicked

    const dates: string[] = []
    for (const day = moment(from); day.isSameOrBefore(to, 'day'); day.add(1, 'day')) {
      dates.push(day.format('YYYY-MM-DD'))
    }
    this.setDates(dates, blackout)
  }

  // Past dates cannot change.
  removeRange(range: AirbnbBlackoutDateRange) {
    this.setDates(
      range.dates.filter((date) => date >= this.today),
      false
    )
  }

  close() {
    if (this.isLoading) return
    this.dialogRef.close()
  }

  save() {
    if (!this.datesToAdd.length && !this.datesToRemove.length) return

    const { connection, discount } = this.data
    // The dialog stays open until Airbnb answers, so the parent always gets the saved status.
    this.isLoading = true
    this.dialogRef.disableClose = true
    this.airbnbService
      .setListingDiscountBlackoutDates(
        connection.propertyId,
        connection.channelId,
        connection.roomId,
        discount.type,
        this.datesToAdd,
        this.datesToRemove
      )
      .pipe(
        finalize(() => {
          this.isLoading = false
          this.dialogRef.disableClose = false
        })
      )
      .subscribe(
        (res) => this.dialogRef.close(res),
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
  }

  private setDates(dates: string[], blackout: boolean) {
    // A change during a save would not be in the request.
    if (this.isLoading) return

    const selected = new Set(this.selected)
    dates.forEach((date) => (blackout ? selected.add(date) : selected.delete(date)))

    // Block only a change that takes a year over its quota, so that a removal is always possible.
    const quotas = getAirbnbBlackoutDatesYearQuotas(this.data.discount, [...selected])
    const exceeded = quotas.find(
      (quota) => quota.left < 0 && quota.left < lodash.find(this.quotas, { year: quota.year })?.left
    )
    if (exceeded) {
      const left = Math.max(lodash.find(this.quotas, { year: exceeded.year })?.left || 0, 0)
      this.toaster.error(
        $localize`Airbnb allows ${exceeded.total} blackout dates per calendar year. ${exceeded.year} has ${left} dates left.`
      )
      return
    }

    this.selected = selected
    this.quotas = quotas
    this.ranges = toAirbnbBlackoutDateRanges([...selected])
    this.datesToAdd = [...selected].filter((date) => !this.savedDates.includes(date)).sort()
    this.datesToRemove = this.savedDates.filter((date) => !selected.has(date)).sort()
    // Renders the month again, so that dateClass marks the changed dates.
    this.calendar.updateTodaysDate()
  }
}
